import { graphHooks } from '@tunasong/graph-lib/react'
import { Poll as PollIcon } from '@tunasong/icons'
import { useCurrentUser } from '@tunasong/redux'
import type { ManyOfManyPollResponse } from '@tunasong/schemas'
import { logger } from '@tunasong/models'
import { isManyOfManyPoll } from '@tunasong/schemas'
import { Typography, FormGroup, FormControl, FormControlLabel } from '@tunasong/ui-lib'
import type { FC } from 'react'
import React, { useMemo, useRef } from 'react'
import invariant from 'tiny-invariant'
import type { PollProps } from '../poll.js'
import styles from '../poll.module.css'
import { getProgress, isCompleteResponse } from '../progress-util.js'
import Progress from '../progress.js'
import { sortPollOptionsIndices } from '../sort.js'
import VoteButton from '../vote-button.js'
import { PollOptionName } from '../poll-option-name.js'
import { PlayAllButton, PollAudioProvider, WithOptionAudio } from '../option-audio.js'

/**
 * Answers save as they are picked and stay editable until the poll closes — there is no Save button. Unsaved
 * picks used to live only in component state and vanished on navigation or reload. Whether an answer is
 * complete is derived (`isCompleteResponse`), never stored.
 */
export const ManyOfManyPollVote: FC<PollProps> = props => {
  const { poll, userId, onChange, sort } = props

  invariant(isManyOfManyPoll(poll), `Poll must be a ManyOfManyPoll`)

  const { userId: currentUserId } = useCurrentUser()
  const { profile: respondent } = graphHooks.useProfile(userId)
  const { createEntity } = graphHooks.useEntityGraph()
  /** Rapid picks coalesce into one save; a pending save is flushed if the voter leaves. */
  const updateEntity = graphHooks.useEntityUpdate<ManyOfManyPollResponse>({ debounceDelay: 500 })

  const { entities: allResponses = [], isSuccess: hasLoadedChildren } =
    graphHooks.useEntityChildren<ManyOfManyPollResponse>({
      parentId: poll.id,
      filter: 'pollresponse',
    })

  const queriedResponse = useMemo(
    () => allResponses.filter(response => response.userId === userId)[0],
    [allResponses, userId]
  )

  const savedResponse = queriedResponse

  /**
   * Picks made in this session, laid over the saved copy. The children query lags every write, so it is a seed,
   * never the truth about what this voter has picked; starting from the saved answer (not `{}`) means a re-edit
   * never clears earlier picks.
   */
  const [draft, setDraft] = React.useState<Record<string, boolean> | undefined>(undefined)
  const currentResponses = draft ?? savedResponse?.choices ?? {}

  /**
   * The one create for this voter, in flight or settled, resolving to the response id. `savedResponse` cannot
   * guard against a second create: it comes from the same lagging read, so every pick before the refetch lands
   * would take the create path and write another pollresponse — and duplicates skew every percentage. It
   * resolves to `undefined` on failure (and clears itself) so the next pick can try again.
   */
  const creation = useRef<Promise<string | undefined> | null>(null)

  const isExternalRespondent = userId !== currentUserId

  invariant(isManyOfManyPoll(poll), 'Poll must be many of many')

  const createResponse = async (choices: Record<string, boolean>) => {
    const entity = { type: 'pollresponse', pollType: 'many-of-many', choices } satisfies ManyOfManyPollResponse
    try {
      const created = await createEntity({ parent: poll, entity }).unwrap()
      return created.id
    } catch (error) {
      creation.current = null
      logger.warn('Failed to create the poll response', error)
      return undefined
    }
  }

  const save = (choices: Record<string, boolean>) => {
    /** One voter, one response: once it exists, every pick updates it. */
    if (savedResponse) {
      updateEntity(savedResponse.id, { choices })
      return
    }
    /** Assigned before anything can await, so two picks in one tick cannot both create. */
    if (!creation.current) {
      creation.current = createResponse(choices)
      return
    }
    /** Picked again while the create was out: update whatever it produced. */
    void creation.current.then(id => {
      if (id) {
        updateEntity(id, { choices })
      }
    })
  }

  const handleChange = (idx: number) => (ev: unknown, val: boolean) => {
    invariant(hasLoadedChildren, 'Children should be loaded')
    const choices = { ...currentResponses, [idx]: val }
    setDraft(choices)
    save(choices)
  }

  const { name = 'Untitled', finalOption, closed } = poll

  const pollOptionsIndices = sort
    ? // Seeded per viewer and poll, so the order holds across re-renders and votes. GH #1968
      sortPollOptionsIndices(poll, allResponses, 'random', `${currentUserId ?? userId}:${poll.id}`)
    : poll.pollOptions.map((_, idx) => idx)

  /** Judged on what this voter sees (their picks this session), so the status follows every pick at once. */
  const shownResponse: ManyOfManyPollResponse = {
    type: 'pollresponse',
    pollType: 'many-of-many',
    choices: currentResponses,
  }
  const isComplete = isCompleteResponse(shownResponse, poll)

  const { responseQuota = Number.MAX_SAFE_INTEGER } = poll
  const numUserResponses = currentResponses
    ? Object.values(currentResponses).reduce((acc, val) => acc + (val === true ? 1 : 0), 0)
    : 0
  const hasReachedQuota = numUserResponses >= responseQuota

  /**
   * Closing the poll is the lock: answers stay editable while it is open, so `allowChange` no longer applies to
   * many-of-many. Picking writes, so another respondent's answers are always read-only.
   */
  const readOnly = Boolean(!onChange || poll.closed || !hasLoadedChildren || isExternalRespondent)

  const responses = currentResponses

  return (
    <PollAudioProvider pollId={poll.id}>
      <div className={styles.container}>
        <Typography variant="subtitle1" className={styles.title}>
          <PollIcon /> {name}
        </Typography>
        <PlayAllButton options={poll.pollOptions} />
        {poll.closed && (
          <Typography variant="caption" color="secondary">
            The Poll is closed.
          </Typography>
        )}
        {!isExternalRespondent && !poll.closed && !isComplete && responseQuota < Number.MAX_SAFE_INTEGER && (
          <Typography variant="caption" display="block">
            You have picked {numUserResponses} of {responseQuota}.
          </Typography>
        )}
        {isComplete ? (
          <Typography variant="caption" color="primary" display="block">
            {isExternalRespondent ? (respondent?.name ?? respondent?.email + ' has') : 'You have'} completed this poll.
          </Typography>
        ) : null}
        <FormControl className={styles.formControl}>
          <FormGroup>
            {hasLoadedChildren &&
              pollOptionsIndices &&
              pollOptionsIndices
                .map(idx => ({ option: poll.pollOptions[idx], idx }))
                .map(({ option, idx }) => (
                  <WithOptionAudio key={idx} option={option}>
                    <FormControlLabel
                      value={idx}
                      control={
                        <VoteButton
                          disabled={readOnly || (hasReachedQuota && !responses?.[idx])}
                          status={responses?.[idx] ?? false}
                          onChange={handleChange(idx)}
                        />
                      }
                      label={
                        <div className={styles.optionLabel}>
                          <PollOptionName option={option} withAudio={false} />
                          {poll.closed && (
                            <Progress value={getProgress(poll, allResponses, idx)} selected={finalOption?.[idx]} />
                          )}
                        </div>
                      }
                      className={styles.formLabel}
                    />
                  </WithOptionAudio>
                ))}
          </FormGroup>
          {!readOnly && !closed ? (
            <Typography variant="caption" color="textSecondary">
              Your choices are saved as you pick them.
            </Typography>
          ) : null}
        </FormControl>
      </div>
    </PollAudioProvider>
  )
}
